/* Jeu d'essai pour la catégorisation assistée.
 *
 * Reproduit les formes réelles fournies par le back pour `start`, `answers`,
 * `complete` et `convertToRules`, afin de dérouler le parcours tant que les
 * endpoints ne sont pas déployés — sans toucher aux composants.
 *
 * Activer :   localStorage.setItem('FYGR_ASSISTED_CATEG_MOCK', '1')
 * Désactiver : localStorage.removeItem('FYGR_ASSISTED_CATEG_MOCK')
 *
 * À supprimer, avec les branches `if (useFixtures())` du store, une fois les
 * endpoints livrés.
 *
 * Note : `parent_id` / `category_id` des `tree_actions` pointent vers des
 * catégories qui n'existent pas dans l'arbre local. Les chemins affichés
 * resteront donc vides en mode jeu d'essai ; ils se résolvent normalement
 * contre l'arbre réel.
 *
 * `start` renvoie la session sous `result.session` : une session déjà ouverte
 * est reprise, pas rejouée.
 */

const MOCK_KEY = 'FYGR_ASSISTED_CATEG_MOCK';

export const useFixtures = () => localStorage.getItem(MOCK_KEY) === '1';

// Assez long pour que les écrans d'attente se voient réellement.
const LATENCY_MS = 2500;

const delayed = (result) =>
  new Promise((resolve) => {
    setTimeout(() => resolve({result}), LATENCY_MS);
  });

const START = {
  session: {
    id: '478c6bee-5b25-4eb4-81f6-511090e2fa81',
    iteration: 1,
    status: 'awaiting_input',
    has_custom_tree: false,
    suggestion_count: 60,
    truncated_suggestion_count: 1,
    created_at: '2026-08-13T10:40:12.776588',
    error_message: null,
  },
  reviewed_count: 60,
  tree_actions: [
    {action_id: 't1', type: 'create_category', name: 'Advertising & marketing',
      parent_id: 'd0202bf2-c142-11f0-aeb3-0242ac110003', category_id: null,
      reason: 'Facebook Ads and LinkedIn spend has no existing category.'},
    {action_id: 't2', type: 'create_category', name: 'Travel & transport',
      parent_id: 'd0202bf2-c142-11f0-aeb3-0242ac110003', category_id: null,
      reason: 'Motorway tolls and airline booking need a travel category.'},
    {action_id: 't3', type: 'create_category', name: 'Online sales',
      parent_id: 'd0201a90-c142-11f0-aeb3-0242ac110003', category_id: null,
      reason: 'Card payouts are a distinct revenue stream from site work.'},
    {action_id: 't4', type: 'rename_category',
      name: 'Marketplace & online purchases', parent_id: null,
      category_id: 'd02026ac-c142-11f0-aeb3-0242ac110003',
      reason: 'Best match for the repeated marketplace purchases.'},
    {action_id: 't5', type: 'rename_category', name: 'Vehicle lease (Athlon)',
      parent_id: null, category_id: 'd0205762-c142-11f0-aeb3-0242ac110003',
      reason: 'Keywords name a specific vehicle leasing arrangement.'},
    {action_id: 't6', type: 'rename_category', name: 'Equipment lease',
      parent_id: null, category_id: 'd02058a2-c142-11f0-aeb3-0242ac110003',
      reason: 'Keywords point to a second, distinct leasing arrangement.'},
  ],
  dropped_suggestions: [
    {suggestion_id: '144a897a-3531-11ef-8b48-0242ac110002',
      reason: 'City name plus contract number, no meaning.'},
    {suggestion_id: 'dad590f4-e26f-11ee-b4fe-0242ac110002',
      reason: '"Web" is too generic to be reliable.'},
    {suggestion_id: 'c77dfd24-3002-11f0-80d7-55b6426bd379',
      reason: '"Payment, services" are generic banking terms.'},
    {suggestion_id: 'cf5054d4-bdf2-11f0-9a48-0242ac110002',
      reason: 'Bare number, no meaning.'},
  ],
  questions: [
    {
      id: 'q1',
      text: 'Should the payouts you receive from online payment processors be counted as your revenue?',
      why_it_matters:
        'These payouts make up the largest share of your transactions, so getting this wrong would distort your entire cash-flow picture.',
      options: [
        {id: 'a', label: 'Yes, count the full amount received as revenue'},
        {id: 'b', label: 'Yes, but show processor fees separately under bank charges'},
        {id: 'c', label: 'No, these are just transfers between my own accounts'},
      ],
    },
    {
      id: 'q2',
      text: 'How should we treat the supermarket and food-delivery transactions (grocery chain, food delivery, and similar)?',
      why_it_matters:
        'These could be goods purchased for resale, staff meals, or personal spending, and each would land in a very different part of your accounts.',
      options: [
        {id: 'a', label: 'Goods purchased for the business to resell'},
        {id: 'b', label: 'Staff meals or entertainment expenses'},
        {id: 'c', label: "Personal spending that shouldn't be counted as a business cost"},
      ],
    },
    {
      id: 'q3',
      text: 'How would you like to track your vehicle and equipment lease payments?',
      why_it_matters:
        'Depending on your answer, these recurring payments will show up either as financing repayments or as ordinary operating costs, which changes how your results read.',
      options: [
        {id: 'a', label: 'As financing repayments, under financial debt'},
        {id: 'b', label: 'As an ordinary operating expense'},
        {id: 'c', label: 'In another category you choose'},
      ],
    },
  ],
};

// En conditions réelles les règles viennent de `getSuggestionsList` ; ici on
// les fournit avec `complete` pour que l'écran 4 ait de quoi s'afficher.
// `requiresCategory` / `renamedByCategory` pointent vers les `action_id`
// ci-dessus, pour exercer le blocage et le repli de libellé.
// `category` est la clé de regroupement de l'écran 4. Plusieurs suggestions
// visent volontairement la même catégorie ici (`cat-services`, qui partage
// « paypal » entre trois d'entre elles) pour que le regroupement et le
// dédoublonnage des mots-clés se voient en mode jeu d'essai.
const RULES = [
  {id: 'r-cb', category: 'cat-cb', leaf: 'Vente en magasin › CB',
    path: 'Recettes › Vente en magasin › CB', affected: 200,
    confidence: 'high', keywords: ['payplug'], checked: true},
  {id: 'r-docusign', category: 'cat-services',
    leaf: 'Prestations de services',
    path: 'Fournisseurs › Prestations de services', affected: 31,
    confidence: 'high', keywords: ['docusigninc', 'paypal'], checked: true},
  {id: 'r-adobe', category: 'cat-services', leaf: 'Prestations de services',
    path: 'Fournisseurs › Prestations de services', affected: 22,
    keywords: ['adobesystem', 'paypal'], checked: true},
  {id: 'r-fiverr', category: 'cat-services', leaf: 'Prestations de services',
    path: 'Fournisseurs › Prestations de services', affected: 12,
    keywords: ['fiverr', 'paypal'], checked: true},
  {id: 'r-online', category: 'cat-online', leaf: 'Online sales',
    path: 'Recettes › Online sales', affected: 90, confidence: 'high',
    keywords: ['stripe', 'sumup'], requiresCategory: 't3', checked: true},
  {id: 'r-market', category: 'cat-market',
    leaf: 'Marketplace & online purchases',
    path: 'Achats › Marketplace & online purchases',
    altLeaf: 'Achats en ligne', altPath: 'Achats › Achats en ligne',
    renamedByCategory: 't4', affected: 37, confidence: 'high',
    keywords: ['amazon', 'cdiscount'], checked: true},
  {id: 'r-salary', category: 'cat-salary', leaf: 'Salaires',
    path: 'Employés › Salaires', affected: 34,
    keywords: ['salaire', 'prime'], checked: true},
];

const COMPLETE = {rules: RULES, coverage: 78};

// Réponse de `convertToRules` : ce qui a effectivement été appliqué.
const CONVERT_TO_RULES = {
  rulesCreated: 5,
  transactionsCategorized: 380,
  failures: [],
};

export const fixtures = {
  start: () => delayed(START),
  answers: () => delayed({status: 'ok'}),
  complete: () => delayed(COMPLETE),
  convertToRules: () => delayed(CONVERT_TO_RULES),
};
